import { useMemo } from "react";
import { Icon } from "@iconify/react";

export const getTaskFormFields = ({ showCostFields = false } = {}) => [
  {
    field: "codeId",
    label: "Task Code",
    type: "select",
    size: 6,
    api: "task-codes",
    displayField: "name",
    required: false,
  },
  {
    field: "modifierCodeId",
    label: "Modifier Code",
    type: "select",
    size: 4,
    api: "modifier-codes",
    displayField: "name",
    isClearable: false,
  },
  { field: "statutory", label: "Compliance Task", type: "switch", size: 2 },
  { field: "name", label: "Task Name", type: "text", size: 12, required: true },
  {
    field: "taskTypeId",
    label: "Task Type",
    type: "select",
    size: 4,
    api: "task-types",
    displayField: "taskTypeName",
    required: true,
  },
  {
    field: "defaultCostCentre",
    label: "Cost Centre",
    type: "select",
    size: 4,
    api: "cost-centres",
    displayField: "costCenterDescription",
    required: true,
  },
  { field: "duration", label: "Task Duration (Hours)", type: "number", size: 4 },
  { field: "labourHours", label: "Labour Hours", type: "number", size: 4 },
  ...(showCostFields
    ? [
      { field: "partsPrice", label: "Parts Price", type: "number", size: 4 },
      { field: "miscPrice", label: "Misc Price", type: "number", size: 4 },
      // { field: "labourCost", label: "Labour Cost", type: "number", size: 2 },
    ]
    : []),
  {
    field: "lastDone",
    label: "Last Known Change",
    type: "number",
    size: 4,
  },
  {
    field: "labourExpense",
    label: "Labour Expense Code",
    type: "select",
    size: 6,
    api: "expense-codes",
    displayField: "name",
    required: true,
  },
  {
    field: "miscExpense",
    label: "Other Expense Code",
    type: "select",
    size: 6,
    api: "expense-codes",
    displayField: "name",
    required: true,
  },
  { field: "reference", label: "External Reference", type: "text", size: 6 },
];

export const useTaskFormFields = (showTaskRefSource) =>
  useMemo(() => getDynamicTaskFormFields(getTaskFormFields(), showTaskRefSource), [showTaskRefSource]);

export const getDynamicTaskFormFields = (taskFormFields, showTaskRefSource) =>
  taskFormFields
    .filter((f) => f.field !== "labourExpense")
    .filter((f) => f.field !== "labour_hours")
    .filter((f) => f.field !== "miscExpense" && f.field !== "misc_price")
    .filter((f) => Number(showTaskRefSource) === 1 || (f.field !== "reference" && f.field !== "source"));

export const buildSchedulingFields = ({
  modalTask,
  schedulingGroupOptions,
  assetMeterOptions,
  plantRateMeasurementUnitOption,
  schedulingTypeOptions,
  forecastBasisOptions,
  schedulingWindowOptions,
  schedulingPriorityOptions,
}) => {
  const planning = Boolean(modalTask?.planningTask);
  const calendar = modalTask?.calendarBased !== undefined ? Boolean(modalTask.calendarBased) : true;
  const fields = [
    { field: "schedulingType", label: "Scheduling Type", type: "radio", size: 4, options: schedulingTypeOptions },
    { field: "forecastBasis", label: "Schedule Basis", type: "radio", size: 4, options: forecastBasisOptions },
    {
      field: "calendarBased",
      label: "Calendar Based?",
      type: "switch",
      size: 2,
      onChange: (nextValue, setFormData, field) => {
        setFormData((prev) => {
          if (!prev) return prev;

          const wasCalendarBased = Number(prev.calendarBased) === 1;
          const updated = { ...prev, [field]: nextValue };

          if (nextValue === 1 && !wasCalendarBased) {
            updated.assetMeterId = null;
          }

          return updated;
        });
      },
    },
    {
      field: "planningTask",
      label: "Planning Task?",
      type: "switch",
      size: 2,
      disabled: modalTask?.schedulingType === "suppression" || modalTask?.schedulingType === "cycle",
    },
  ];

  if (planning) {
    fields.push({
      field: "frequency",
      label: calendar ? "Frequency Days" : "Frequency",
      type: "number",
      size: 2,
      required: true,
      step: 1,
      inputMode: "numeric",
    });


    if (!calendar) {
      const isPlantHireTask = modalTask?.plantRateId !== undefined && modalTask?.plantRateId !== null;
      console.log('isPlantHireTask', isPlantHireTask);
      if (isPlantHireTask) {
        fields.push({
          field: "plantMeterId",
          label: "Meter",
          type: "select",
          size: 3,
          required: modalTask?.plantRateId !== null && Number(modalTask?.calendarBased) === 0,
          options: [{ value: 1, label: 'Hours' }],
        });
      } else {
      fields.push({
        field: "assetMeterId",
        label: "Meter",
        type: "select",
        size: 3,
        required: true,
        options: assetMeterOptions,
        formatOptionLabel: (opt) => `${opt.label} (${opt.unitCode})`,
      });
      }
    }
  }

  if (planning) {
    fields.push(
      { field: "planningHorizon", label: "Lead Time", type: "select", size: 3, api: "planning-horizon", displayField: "name", required: true },
    );
  }

  fields.push(
    {
      field: "schedulingGroupId",
      label: "Scheduling Group",
      type: "select",
      size: 4,
      options: schedulingGroupOptions.map((g) => ({ value: g.id, label: g.name, icon: g.icon })),
      formatOptionLabel: (opt) => (
        <span className="d-flex align-items-center gap-2">
          <Icon icon={opt.icon} className="text-xxl text-primary" style={{ position: "relative", top: "-1px" }} /> {opt.label}
        </span>
      ),
      required: true,
    },
    { field: "schedulingWindow", label: "Scheduling Window", type: "select", size: 4, options: schedulingWindowOptions, required: true },
    { field: "schedulingPriority", label: "Scheduling Priority", type: "select", size: 4, options: schedulingPriorityOptions },
  );

  return fields.filter(
    (f) =>
      modalTask?.schedulingType === "suppression" ||
      !["schedulingGroupId", "schedulingWindow", "schedulingPriority"].includes(f.field),
  );
};
